import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";

export type ReviewStatus = "PENDING" | "APPROVED" | "REJECTED";

export interface Review {
  id: number;
  score: number;
  review: string | null;
  status?: ReviewStatus;
  rejectionReason?: string | null;
  userId: number;
  courseId: number;
  createdAt: string;
  updatedAt: string;
  /** Sharh muallifi (ro'yxatni o'qiganda qo'shib beriladi). */
  user?: { id: number; firstName: string; lastName: string; avatarUrl: string | null };
  /** Kurs (moderatsiya o'qishlarida qo'shib beriladi). */
  course?: { id: number; title: string; thumbnail: string | null };
}

export interface ReviewModerationStats {
  pending: number;
  approved: number;
  rejected: number;
}

export interface UpsertReviewData {
  score: number;
  review?: string;
}

export interface ReviewFilter {
  page?: number;
  limit?: number;
  status?: ReviewStatus;
}

/** Kurs sharhlari API — backend'dagi `ReviewsController` ning ko'zgusi. */
export class ReviewsApi extends BaseApi {
  private url(courseId: number): string {
    return `/courses/${courseId}/reviews`;
  }

  /** Kurs sharhlarining ommaviy ro'yxati. */
  getForCourse(courseId: number, filter: ReviewFilter) {
    return this.request.get<Review[], Pagination<Review>>(this.url(courseId), { params: filter });
  }

  /** Kursga yozgan o'z sharhlarim. */
  getMine(courseId: number) {
    return this.request.get<Review[], ApiResponse<Review[]>>(`${this.url(courseId)}/me`);
  }

  /** Yangi sharh yozadi (faqat yozilgan o'quvchilar; bir nechtasi mumkin). */
  create(courseId: number, data: UpsertReviewData) {
    return this.request.post<Review, ApiResponse<Review>>(this.url(courseId), data);
  }

  /** O'z sharhlarimdan birini tahrirlaydi. */
  update(courseId: number, id: number, data: UpsertReviewData) {
    return this.request.patch<Review, ApiResponse<Review>>(`${this.url(courseId)}/${id}`, data);
  }

  /** O'z sharhlarimdan birini o'chiradi. */
  remove(courseId: number, id: number) {
    return this.request.delete<null, ApiResponse<null>>(`${this.url(courseId)}/${id}`);
  }

  // ─── Moderatsiya (xodimlar) ─────────────────────────────────────────────────

  moderationStats() {
    return this.request.get<ReviewModerationStats, ApiResponse<ReviewModerationStats>>(
      "/moderation/reviews/stats",
    );
  }

  moderationList(filter: ReviewFilter) {
    return this.request.get<Review[], Pagination<Review>>("/moderation/reviews", {
      params: filter,
    });
  }

  approve(id: number) {
    return this.request.post<Review, ApiResponse<Review>>(`/moderation/reviews/${id}/approve`, {});
  }

  reject(id: number, reason: string) {
    return this.request.post<Review, ApiResponse<Review>>(`/moderation/reviews/${id}/reject`, {
      reason,
    });
  }
}

export const reviewsApi = new ReviewsApi();
